import { Badge, Card, EmptyState, Skeleton, SkeletonCard } from "@abbiamo/ui";
import { getPortalRedirectTarget } from "@seller-hermes/features/portal/portal-redirects";
import { setActiveSellerGroupId } from "@seller-hermes/features/portal/portal-local-context";
import { hostOf, track } from "@seller-hermes/app/telemetry";
import {
  getPortalProductLabel,
  openPortalProduct,
} from "@seller-hermes/features/portal/portal-products";
import { PortalService } from "@seller-hermes/features/portal/portal.api";
import {
  PortalMeResponse,
  PortalProfile,
  PortalSellerGroupContext,
} from "@seller-hermes/features/portal/portal.types";
import {
  ArrowRight,
  ArrowUpRight,
  FlaskConical,
  Loader2,
  LogOut,
  Users,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

export const AUTO_REDIRECT_DELAY = 700;

export const profileLabel = {
  LOG: "LOG - Orquestrador",
  GO: "GO - Frota Própria",
  PAY: "PAY",
} as const;

// Portal profiles map onto official Abbiamo design-system products:
// LOG → group (green), GO → go (yellow), PAY → pay (blue).
type DsProduct = "group" | "go" | "pay";

export const profileProduct: Record<PortalProfile, DsProduct> = {
  LOG: "group",
  GO: "go",
  PAY: "pay",
};

// Official Abbiamo product symbols (from the design-system logo set).
export const profileIconSrc: Record<PortalProfile, string> = {
  LOG: "/logos/group_icon.png",
  GO: "/logos/go_icon.png",
  PAY: "/logos/pay_icon.png",
};

// Static class maps — Tailwind cannot see dynamically-built class names, so
// every product variant must appear as a full literal somewhere it scans.
const PULSE_BG: Record<DsProduct, string> = {
  group: "bg-ab-group",
  go: "bg-ab-go",
  pay: "bg-ab-pay",
};

// Handoff do dominio legado: o dashboard antigo redireciona para ca carregando
// o seller group em que o usuario estava (users.seller_group_id). Nao da para
// resolver isso por is_default, que vem de users.default_seller_group_id — uma
// coluna da era do portal, nula para quem nunca passou por aqui.
export const REQUESTED_CONTEXT_PARAM = "sg";

const getRequestedSellerGroupId = (): string | null => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(
    REQUESTED_CONTEXT_PARAM,
  );
};

const getAvailableContexts = (portalMe: PortalMeResponse) =>
  portalMe.seller_groups.filter(
    (context) =>
      context.profile !== "PAY" && getPortalRedirectTarget(context).isAvailable,
  );

/**
 * True quando veio um seller group na URL e a sessao atual nao tem acesso a
 * ele. O portal segura o usuario na tela de selecao nesse caso, em vez de
 * manda-lo para outro ambiente.
 */
export const isRequestedContextDenied = (
  portalMe: PortalMeResponse,
): boolean => {
  const requested = getRequestedSellerGroupId();
  if (!requested) return false;

  return !getAvailableContexts(portalMe).some(
    (context) => context.id === requested,
  );
};

export const getPortalDecision = (portalMe: PortalMeResponse) => {
  if (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("select_context") === "true"
  ) {
    return null;
  }

  const availableContexts = getAvailableContexts(portalMe);

  // O parametro nao precisa ser assinado: ele so escolhe entre contextos que o
  // /portal/me ja autorizou para esta sessao. Um id forjado nao casa com nada
  // e cai no seletor. Quando veio um pedido explicito e ele nao e acessivel,
  // NAO caimos para is_default — mandar o usuario para um ambiente que ele nao
  // pediu seria pior do que deixa-lo escolher.
  const requestedSellerGroupId = getRequestedSellerGroupId();
  if (requestedSellerGroupId) {
    return (
      availableContexts.find(
        (context) => context.id === requestedSellerGroupId,
      ) ?? null
    );
  }

  const defaultContext = availableContexts.find(
    (context) => context.is_default,
  );

  if (defaultContext) {
    return defaultContext;
  }

  const availableProductionContexts = availableContexts.filter(
    (context) => !context.is_homologation,
  );

  // Only auto-redirect when there is truly a single seller group — promo or
  // unavailable cards alongside the available one should still show the
  // selection screen so the user sees what is available vs what is coming.
  if (
    availableProductionContexts.length === 1 &&
    portalMe.seller_groups.length === 1
  ) {
    return availableProductionContexts[0];
  }

  return null;
};

export const getDefaultContextDescription = (
  context: PortalSellerGroupContext,
) => {
  if (context.profile === "LOG") {
    return context.is_homologation
      ? "Ambiente de homologacao para validar operacoes do Orquestrador."
      : "Automacao de despacho, regras de frete, retire em loja, reversa e disputas. Padronizacao total da sua operacao.";
  }

  if (context.profile === "GO") {
    return context.is_homologation
      ? "Ambiente de homologacao para validar rotas e motoristas."
      : "Automatize sua frota: gestao de despacho, crowdshipping e ofertas, roteirizacao e app do entregador.";
  }

  return "Pague motoristas, transportadoras e a Abbiamo em um so lugar.";
};

// Local development has no log/go subdomains, so it uses query-string
// handoff. Real Abbiamo hosts use shared cookies and stable URLs.
export const getPortalRedirectMode = () => {
  if (typeof window === "undefined") return "production";

  return ["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? "local"
    : "production";
};

export const openPortalContext = (context: PortalSellerGroupContext) => {
  const target = getPortalRedirectTarget(context, getPortalRedirectMode());

  if (!target.isAvailable) return;

  setActiveSellerGroupId(context.id, context.profile);
  // Este caminho depende só do cookie compartilhado da sessão: o evento leva
  // quantos cookies do Cognito existem AGORA, que é o que o produto vai ler.
  track({
    name: "portal_product_redirect",
    product: context.profile,
    handoff: "shared_cookie",
    target_host: hostOf(target.url),
  });
  window.location.replace(target.url);
};

// ─── Components ───────────────────────────────────────────────────────────────

interface PortalContextCardProps {
  context: PortalSellerGroupContext;
  disabled?: boolean;
  onSelect: (context: PortalSellerGroupContext) => void;
}

export const PortalContextCard = ({
  context,
  disabled,
  onSelect,
}: PortalContextCardProps) => {
  const product = profileProduct[context.profile];
  const target = getPortalRedirectTarget(context);
  const isUnavailable = !target.isAvailable;
  const actionLabel = isUnavailable
    ? target.unavailableReason || "Em breve"
    : "Entrar";
  const visibleProducts =
    context.products?.filter((link) => link.product !== "INSIGHTS") ?? [];

  return (
    <Card
      className={`overflow-hidden ${isUnavailable ? "opacity-90" : ""}`}
      data-ui="product-card"
    >
      <button
        type="button"
        className="group flex w-full flex-col gap-ab-4 rounded-[18px] p-ab-6 text-left transition-colors duration-ab-fast hover:bg-eui-row-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ab-ring disabled:pointer-events-none disabled:cursor-default disabled:hover:bg-transparent"
        onClick={() => onSelect(context)}
        disabled={isUnavailable || disabled}
        aria-disabled={isUnavailable || disabled}
      >
        <span className="flex items-start justify-between gap-ab-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-ab-bg-subtle">
            <img
              src={profileIconSrc[context.profile]}
              alt=""
              className="h-8 w-8 object-contain"
            />
          </span>
          <span className="flex flex-wrap items-center justify-end gap-ab-1.5">
            <Badge variant={isUnavailable ? `${product}-soft` : product}>
              {profileLabel[context.profile]}
            </Badge>
            {context.is_homologation ? (
              <Badge variant="outline">
                <FlaskConical size={12} aria-hidden />
                Homologacao
              </Badge>
            ) : null}
          </span>
        </span>

        <span className="flex flex-col gap-ab-1">
          <span className="text-ab-xl font-ab-semibold text-ab-fg">
            {context.name}
          </span>
          <span className="text-ab-base text-ab-fg-muted">
            {context.description || getDefaultContextDescription(context)}
          </span>
        </span>

        <span className="mt-ab-2 flex items-center justify-between gap-ab-2">
          {context.metric_label ? (
            <span className="text-ab-sm text-ab-fg-muted">
              {context.metric_label}
            </span>
          ) : null}
          <span
            className={`ml-auto inline-flex h-8 items-center gap-ab-1 rounded-full text-ab-base font-ab-medium transition-colors duration-ab-fast ${
              isUnavailable
                ? "text-ab-fg-muted"
                : "bg-eui-fg px-ab-3 text-eui-bg group-hover:bg-eui-fg/90"
            }`}
          >
            {actionLabel}
            {!isUnavailable ? (
              <ArrowRight
                size={16}
                aria-hidden
                className="transition-transform duration-ab-fast group-hover:translate-x-0.5"
              />
            ) : null}
          </span>
        </span>
      </button>

      {/* Insights só é acessível via redirect a partir do próprio produto
          (entrada fria) — nunca aparece como opção no portal normal. */}
      {visibleProducts.length ? (
        <div className="flex items-center gap-ab-4 border-t border-ab border-ab-border px-ab-6 py-ab-3">
          {visibleProducts.map((link) => (
            <button
              key={link.product}
              type="button"
              className="inline-flex items-center gap-ab-1 rounded-ab-sm text-ab-sm font-ab-medium text-ab-fg-muted transition-colors duration-ab-fast hover:text-ab-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ab-bg disabled:opacity-40"
              disabled={disabled}
              onClick={() => openPortalProduct(context, link)}
            >
              <ArrowUpRight size={12} aria-hidden />
              {getPortalProductLabel(link)}
            </button>
          ))}
        </div>
      ) : null}

      {context.homologation_label ? (
        <div className="border-t border-ab border-ab-border px-ab-6 py-ab-3">
          <button
            type="button"
            className="inline-flex items-center gap-ab-1 rounded-ab-sm text-ab-sm font-ab-medium text-ab-fg-muted transition-colors duration-ab-fast hover:text-ab-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ab-bg disabled:opacity-40"
            disabled={disabled}
            onClick={() =>
              onSelect({
                ...context,
                id: context.homologation_id ?? context.id,
                user_seller_group_id:
                  context.homologation_user_seller_group_id ??
                  context.user_seller_group_id,
                is_homologation: true,
              })
            }
          >
            <FlaskConical size={12} aria-hidden />
            {context.homologation_label}
          </button>
        </div>
      ) : null}
    </Card>
  );
};

interface PortalStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}

export const PortalState = ({
  icon,
  title,
  description,
  action,
}: PortalStateProps) => (
  <EmptyState
    variant="quiet"
    icon={icon}
    title={title}
    description={description}
    action={action}
  />
);

interface PortalShellProps {
  children: React.ReactNode;
}

export const PortalShell = ({ children }: PortalShellProps) => (
  <div className="flex w-full justify-center">{children}</div>
);

export const PortalLoadingState = () => (
  <main className="flex min-h-screen items-center justify-center bg-ab-bg p-ab-6">
    <div className="w-full max-w-[56rem]" aria-label="Carregando operacoes">
      <Skeleton className="h-7 w-1/2 rounded-ab" />
      <Skeleton className="mt-ab-2 h-4 w-1/3 rounded-ab" />
      <div className="mt-ab-6 grid gap-ab-4 sm:grid-cols-2 lg:grid-cols-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  </main>
);

export const PortalRedirectState = ({
  context,
}: {
  context: PortalSellerGroupContext;
}) => {
  const target = getPortalRedirectTarget(context, getPortalRedirectMode());
  const title = `Entrando no ${target.label}`;
  const description = `Redirecionando para ${target.url}.`;

  return (
    <PortalState
      icon={<Loader2 className="animate-spin" size={28} aria-hidden />}
      title={title}
      description={description}
    />
  );
};

export const PortalAutoRedirectState = ({
  context,
  onOpenNow,
}: {
  context: PortalSellerGroupContext;
  onOpenNow: () => void;
}) => {
  const title = `Entrando no ${context.name}...`;

  return (
    <div
      className="mx-auto w-full max-w-[28rem] rounded-[20px] border border-ab border-ab-border bg-ab-card p-ab-8 text-center"
      role="status"
    >
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-[14px] bg-ab-bg-subtle">
        <img
          src={profileIconSrc[context.profile]}
          alt=""
          className="h-8 w-8 object-contain"
        />
      </span>
      <h1 className="mt-ab-4 text-ab-xl font-ab-semibold text-ab-fg">{title}</h1>
      <p className="mt-ab-1 text-ab-base text-ab-fg-muted">
        Esta conta esta definida como padrao, entao o portal pula a tela de
        escolha.
      </p>
      <div className="mt-ab-6 h-1.5 w-full overflow-hidden rounded-ab-full bg-ab-muted">
        <div
          className={`h-full w-2/3 animate-ab-pulse rounded-ab-full ${PULSE_BG[profileProduct[context.profile]]}`}
        />
      </div>
      <button
        type="button"
        className="mt-ab-6 inline-flex h-9 items-center justify-center rounded-full bg-eui-fg px-ab-4 text-ab-base font-ab-medium text-eui-bg transition-[transform,background-color] duration-ab-fast hover:bg-eui-fg/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ab-bg active:scale-[0.97]"
        onClick={onOpenNow}
      >
        Abrir agora
      </button>
    </div>
  );
};

export const PortalSelection = ({
  portalMe,
}: {
  portalMe: PortalMeResponse;
}) => {
  const autoRedirectContext = getPortalDecision(portalMe);
  const [selectedContext, setSelectedContext] =
    React.useState<PortalSellerGroupContext | null>(null);
  const [shouldSetDefault, setShouldSetDefault] = React.useState(false);
  const [savingDefaultContextId, setSavingDefaultContextId] = React.useState<
    string | null
  >(null);
  const [defaultError, setDefaultError] = React.useState<string | null>(null);

  const selectContext = React.useCallback(
    async (nextContext: PortalSellerGroupContext) => {
      if (!getPortalRedirectTarget(nextContext).isAvailable) return;

      setDefaultError(null);

      if (shouldSetDefault) {
        setSavingDefaultContextId(nextContext.id);
        if (!nextContext.user_seller_group_id) {
          setDefaultError(
            "Nao foi possivel identificar o vinculo do ambiente padrao.",
          );
          setSavingDefaultContextId(null);
          return;
        }

        try {
          await PortalService.updateDefaultSellerGroup(
            nextContext.user_seller_group_id,
          );
        } catch {
          setDefaultError(
            "Nao foi possivel salvar o ambiente padrao. Tente novamente.",
          );
          setSavingDefaultContextId(null);
          return;
        }
      }

      setSelectedContext(nextContext);
      openPortalContext(nextContext);
    },
    [shouldSetDefault],
  );

  React.useEffect(() => {
    if (!autoRedirectContext) return;

    const timeoutId = window.setTimeout(() => {
      setSelectedContext(autoRedirectContext);
      openPortalContext(autoRedirectContext);
    }, AUTO_REDIRECT_DELAY);

    return () => window.clearTimeout(timeoutId);
  }, [autoRedirectContext]);

  if (selectedContext) {
    return <PortalRedirectState context={selectedContext} />;
  }

  if (autoRedirectContext) {
    return (
      <PortalAutoRedirectState
        context={autoRedirectContext}
        onOpenNow={() => {
          setSelectedContext(autoRedirectContext);
          openPortalContext(autoRedirectContext);
        }}
      />
    );
  }

  return (
    <section className="w-full max-w-[56rem]" aria-labelledby="portal-title">
      <header className="mb-ab-8 flex flex-col gap-ab-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-ab-3">
          <img
            src="/logos/abbiamo_icon.png"
            alt=""
            className="h-10 w-10 flex-shrink-0 object-contain"
          />
          <div className="min-w-0">
            <p className="truncate text-ab-base text-ab-fg-muted">
              {portalMe.account?.name || "Conta Abbiamo"}
            </p>
            <h1
              className="text-ab-xl font-ab-semibold text-ab-fg"
              id="portal-title"
            >
              Onde voce quer comecar?
            </h1>
          </div>
        </div>
        <div className="-mr-ab-3 flex flex-shrink-0 items-center gap-ab-1 max-sm:-ml-ab-3">
          <Link
            data-ui="nav-link"
            to="/users"
          >
            <Users size={16} aria-hidden />
            Usuarios
          </Link>
          <Link
            data-ui="nav-link"
            to="/logout"
          >
            <LogOut size={16} aria-hidden />
            Sair
          </Link>
        </div>
      </header>

      <div className="grid gap-ab-4 sm:grid-cols-2 lg:grid-cols-3">
        {portalMe.seller_groups.map((context) => (
          <PortalContextCard
            key={`${context.id}-${context.profile}-${context.is_homologation ? "homologation" : "production"}`}
            context={context}
            disabled={Boolean(savingDefaultContextId)}
            onSelect={selectContext}
          />
        ))}
      </div>

      <label className="mt-ab-6 inline-flex cursor-pointer items-center gap-ab-2 text-ab-base text-ab-fg-muted">
        <input
          type="checkbox"
          className="h-4 w-4 rounded-ab-sm border-ab-border"
          checked={shouldSetDefault}
          disabled={Boolean(savingDefaultContextId)}
          onChange={(event) => {
            setDefaultError(null);
            setShouldSetDefault(event.currentTarget.checked);
          }}
        />
        Abrir esta conta por padrao na proxima vez
      </label>

      {defaultError ? (
        <p className="mt-ab-4 text-ab-base font-ab-medium text-ab-destructive" role="alert">
          {defaultError}
        </p>
      ) : null}
    </section>
  );
};
